import { ContactDetailsSectionWebComponent } from '../component/ContactDetailsSectionWebComponent';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../../framework/BaseWebComponentController';
import { ContactDetailsSectionView } from '../view/ContactDetailsSectionView';
import { templateMdrContactDetailsSection } from '../view/template';
import { MdrMandatoryContactInformation } from '../../../../types/company-data';
import { ButtonEvents } from '../../../../../ui/gzButton/types';
import { ContactDetailsSectionDataProvider } from '../dataProvider/ContactDetailsSectionDataProvider';
import { MdrContactDetailsSource, MdrContactImportSource } from '../../common/types';
import { ContactDetailsSectionViewData } from '../types/ContactDetailsSectionViewData';
import { CompanyActionContext } from '../../../types/CompanyActionContext';
import { MdrContactDetailsFormEvents, MdrContactDetailsImportConfig } from '../../mdrContactDetailsForm/types';
import { isEmpty } from 'lodash';

export class ContactDetailsSectionController extends BaseWebComponentController {
  public component: ContactDetailsSectionWebComponent;
  public view: ContactDetailsSectionView;
  public viewConstructor: typeof ContactDetailsSectionView;

  constructor(component: ContactDetailsSectionWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: ContactDetailsSectionView,
      viewTemplate: templateMdrContactDetailsSection,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        // Prevent the 'close' event emitted by import dropdowns from being caught by parents
        // and being interpreted as different 'close' events (e.g. SideMenuEvents.CLOSE)
        element: this.view.dom.$importOptions,
        event: 'close',
        callback: (event: WebComponentEvent) => event.stopPropagation(),
      },
      {
        element: this.view.dom.$importButton,
        event: ButtonEvents.CLICK,
        callback: this.onImportButtonClick,
      },
      {
        element: this.view.dom.$contactForm,
        event: MdrContactDetailsFormEvents.CLEAR_DATA,
        callback: this.onClearDataButtonClick,
      },
    ];
  }

  public async getDataForView(): Promise<ContactDetailsSectionViewData> {
    const defaultViewData: ContactDetailsSectionViewData = {
      context: CompanyActionContext.Create,
      isInPopUp: true,
    };

    return this.component.viewData ?? defaultViewData;
  }

  private async onImportButtonClick(): Promise<void> {
    let selectedContact: MdrMandatoryContactInformation | null;
    const dataProvider = new ContactDetailsSectionDataProvider();

    const importOption: MdrContactImportSource = this.view.dom.$importOptions.value;

    if (!importOption || !importOption.importSource) {
      return;
    }

    switch (importOption.importSource) {
      case MdrContactDetailsSource.PARTNER_CONTACT:
        const importedCompanyId = importOption.importAccount.companyId;
        selectedContact = await dataProvider.getCompanyMdrContactInformation(importedCompanyId);

        if (!isEmpty(selectedContact)) {
          this.view.dom.$importOptions.setStoredFormGroupValue({ importedCompanyId });
        }

        break;

      case MdrContactDetailsSource.GZ_ACCOUNT:
        const importedAccountId = importOption.importAccount.accountId as string;
        selectedContact = this.view.dom.$importOptions.selectedAccount;

        if (!isEmpty(selectedContact)) {
          this.view.dom.$importOptions.setStoredFormGroupValue({ importedAccountId });
        }

        break;

      default:
        throw new Error('Not implemented');
    }

    if (!selectedContact) {
      return;
    }

    const importConfig = this.buildMdrContactDetailsImportConfig(importOption, selectedContact);
    this.view.dom.$contactForm.importContactDetails(importConfig);

    this.view.dom.$importOptions.resetForm();
  }

  private buildMdrContactDetailsImportConfig(
    importOption: MdrContactImportSource,
    selectedContact: MdrMandatoryContactInformation
  ): MdrContactDetailsImportConfig {
    const hasImportedFromPartner = importOption.importSource === MdrContactDetailsSource.PARTNER_CONTACT;

    // selectedContact can be an empty object/array returned from backend when MDR Contact Details are not defined
    const importConfig: MdrContactDetailsImportConfig = {
      hasVerifiedEmailAddress: hasImportedFromPartner || selectedContact.hasVerifiedEmail,
      fullName: selectedContact.fullName ?? '',
      email: selectedContact.email ?? '',
    };

    // phoneNumber can be: undefined, null, or defined. When defined, subfields can be: null, empty strings, or valid values
    if (selectedContact.phoneNumber && !isEmpty(selectedContact.phoneNumber.subscriberNumber)) {
      // Preserve backwards compatibility for phone numbers not having country code specified
      importConfig.phoneNumber = {
        countryCode: selectedContact.phoneNumber.countryCode ?? '',
        subscriberNumber: selectedContact.phoneNumber.subscriberNumber,
      };
    }

    return importConfig;
  }

  private onClearDataButtonClick(): void {
    this.view.dom.$importOptions.setStoredFormGroupValue({});
  }
}
